import React, { useState, useMemo } from "react";
import { Box, Typography, Container, Grid, Slider, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { ArrowRight, Info } from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────────
   CALCULATION CONSTANTS
   The 5%–20% improvement is a NirogGyan marketing benchmark carried over from
   the original prototype. Update these two values if real client data arrives.
   ───────────────────────────────────────────────────────────────────────── */
const RETENTION_BOOST_MIN = 0.05; // +5%
const RETENTION_BOOST_MAX = 0.2; // +20%
const MONTHS_IN_YEAR = 12;

// Number formatters (Indian numbering system)
const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});
const numberFormatter = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 0,
});

// Slider config: [min, max, step, default]
const SLIDERS = {
  volume: { min: 500, max: 50000, step: 500, default: 10000 },
  aov: { min: 300, max: 5000, step: 100, default: 1500 },
  retention: { min: 5, max: 60, step: 1, default: 25 },
};

// ── Styled blue slider matching the site's brand ──────────────────────────
const BlueSlider = styled(Slider)(() => ({
  color: "#1D47A4",
  height: 6,
  padding: "16px 0",
  "& .MuiSlider-rail": {
    backgroundColor: "#E2E8F0",
    opacity: 1,
    height: 6,
  },
  "& .MuiSlider-track": {
    border: "none",
    height: 6,
    background: "linear-gradient(90deg, #1D47A4 0%, #4673D9 100%)",
  },
  "& .MuiSlider-thumb": {
    height: 24,
    width: 24,
    backgroundColor: "#fff",
    border: "3px solid #1D47A4",
    boxShadow: "0 2px 8px rgba(29,71,164,0.35)",
    transition: "box-shadow 0.2s ease",
    "&:hover, &.Mui-focusVisible": {
      boxShadow: "0 0 0 8px rgba(29,71,164,0.16)",
    },
    "&.Mui-active": {
      boxShadow: "0 0 0 12px rgba(29,71,164,0.16)",
    },
  },
}));

const InputCard = styled(Box)(({ theme }) => ({
  background: "#FFFFFF",
  borderRadius: "20px",
  padding: "32px",
  boxShadow: "0 10px 40px rgba(29,71,164,0.08)",
  border: "1px solid rgba(64,123,255,0.10)",
  [theme.breakpoints.down("sm")]: {
    padding: "22px 20px",
    borderRadius: "16px",
  },
}));

const ResultsPanel = styled(Box)(({ theme }) => ({
  position: "relative",
  background: "linear-gradient(160deg, #1D47A4 0%, #0D2847 100%)",
  borderRadius: "18px",
  padding: "22px",
  color: "#fff",
  boxShadow: "0 24px 60px rgba(13,40,71,0.40)",
  border: "1px solid rgba(255,255,255,0.10)",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    padding: "18px 16px",
    borderRadius: "16px",
  },
}));

// Single input control (label + value badge + subtext + slider)
const InputControl = ({ label, subtext, valueText, sliderProps, onChange }) => (
  <Box sx={{ mb: { xs: 3.5, md: 4 }, "&:last-child": { mb: 0 } }}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 1.5,
        mb: 0.5,
      }}
    >
      <Typography
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: { xs: "15px", md: "16px" },
          fontWeight: 600,
          color: "#0F172A",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: { xs: "16px", md: "18px" },
          fontWeight: 700,
          color: "#1D47A4",
          background: "rgba(64,123,255,0.10)",
          padding: "4px 14px",
          borderRadius: "8px",
          whiteSpace: "nowrap",
        }}
      >
        {valueText}
      </Typography>
    </Box>
    <Typography
      sx={{
        fontFamily: "Inter, sans-serif",
        fontSize: { xs: "12.5px", md: "13.5px" },
        color: "#64748B",
        mb: 0.5,
        lineHeight: 1.5,
      }}
    >
      {subtext}
    </Typography>
    <BlueSlider {...sliderProps} onChange={onChange} valueLabelDisplay="off" />
  </Box>
);

const RoiCalculatorSection = ({ onBookDemo }) => {
  const [volume, setVolume] = useState(SLIDERS.volume.default);
  const [aov, setAov] = useState(SLIDERS.aov.default);
  const [retention, setRetention] = useState(SLIDERS.retention.default);

  // ── Core calculation (new logic — retention drives the result) ──────────
  const results = useMemo(() => {
    const annualVolume = volume * MONTHS_IN_YEAR;
    const returningPatients = annualVolume * (retention / 100);
    const extraPatientsMin = Math.round(returningPatients * RETENTION_BOOST_MIN);
    const extraPatientsMax = Math.round(returningPatients * RETENTION_BOOST_MAX);
    const extraRevenueMin = extraPatientsMin * aov;
    const extraRevenueMax = extraPatientsMax * aov;

    return {
      returningPatients: Math.round(returningPatients),
      extraPatientsMin,
      extraPatientsMax,
      extraRevenueMin,
      extraRevenueMax,
    };
  }, [volume, aov, retention]);

  return (
    <Box
      id="calculator"
      sx={{
        width: "100%",
        py: { xs: 5, md: 8 },
        scrollMarginTop: "90px",
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <Grid container spacing={{ xs: 3, md: 5 }} alignItems="stretch">
          {/* LEFT — Inputs */}
          <Grid item xs={12} md={7}>
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              sx={{ height: "100%" }}
            >
              <InputCard sx={{ height: "100%" }}>
                <Typography
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: { xs: "18px", md: "22px" },
                    fontWeight: 700,
                    color: "#1D47A4",
                    mb: 0.5,
                  }}
                >
                  Tell us about your lab
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: { xs: "13px", md: "14px" },
                    color: "#64748B",
                    mb: { xs: 3, md: 4 },
                  }}
                >
                  Drag the sliders to match your numbers. Results update instantly.
                </Typography>

                <InputControl
                  label="Monthly Patient Volume"
                  subtext="Average number of patients visiting your diagnostic lab monthly."
                  valueText={numberFormatter.format(volume)}
                  sliderProps={{
                    value: volume,
                    min: SLIDERS.volume.min,
                    max: SLIDERS.volume.max,
                    step: SLIDERS.volume.step,
                    "aria-label": "Monthly Patient Volume",
                  }}
                  onChange={(_, v) => setVolume(v)}
                />

                <InputControl
                  label="Average Revenue Per Patient (AOV)"
                  subtext="Average ticket size or invoice amount per patient visit."
                  valueText={currencyFormatter.format(aov)}
                  sliderProps={{
                    value: aov,
                    min: SLIDERS.aov.min,
                    max: SLIDERS.aov.max,
                    step: SLIDERS.aov.step,
                    "aria-label": "Average Revenue Per Patient",
                  }}
                  onChange={(_, v) => setAov(v)}
                />

                <InputControl
                  label="Current Patient Retention Rate"
                  subtext="Percentage of patients who currently return for future lab tests."
                  valueText={`${retention}%`}
                  sliderProps={{
                    value: retention,
                    min: SLIDERS.retention.min,
                    max: SLIDERS.retention.max,
                    step: SLIDERS.retention.step,
                    "aria-label": "Current Patient Retention Rate",
                  }}
                  onChange={(_, v) => setRetention(v)}
                />
              </InputCard>
            </Box>
          </Grid>

          {/* RIGHT — Results */}
          <Grid item xs={12} md={5} sx={{ width: "100%", display: "flex" }}>
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              aria-live="polite"
              sx={{ width: "100%", display: "flex" }}
            >
              <ResultsPanel sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", flex: 1 }}>
                {/* Decorative glow */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "-60px",
                    right: "-60px",
                    width: "200px",
                    height: "200px",
                    background: "radial-gradient(circle, rgba(70,115,217,0.45) 0%, transparent 70%)",
                    borderRadius: "50%",
                    pointerEvents: "none",
                  }}
                />

                {/* Title */}
                <Typography
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: { xs: "16px", md: "17px" },
                    fontWeight: 700,
                    color: "#FFFFFF",
                    mb: { xs: 1.5, md: 1.75 },
                    pb: { xs: 1.25, md: 1.5 },
                    borderBottom: "1px solid rgba(255,255,255,0.15)",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  Your Growth With Smart Reports
                </Typography>

                {/* Extra patients who come back */}
                <Box sx={{ position: "relative", zIndex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75, flexWrap: "wrap" }}>
                    <Typography
                      sx={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: { xs: "12.5px", md: "13px" },
                        fontWeight: 700,
                        color: "#FFFFFF",
                      }}
                    >
                      Extra patients who come back
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        background: "rgba(52,211,153,0.20)",
                        color: "#5EEAD4",
                        padding: "2px 8px",
                        borderRadius: "7px",
                        fontSize: "10px",
                        fontWeight: 700,
                        fontFamily: "Inter, sans-serif",
                        whiteSpace: "nowrap",
                      }}
                    >
                      +5% to +20%
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: { xs: "17px", sm: "19px", md: "20px" },
                      fontWeight: 700,
                      color: "#FFFFFF",
                      lineHeight: 1.15,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {numberFormatter.format(results.extraPatientsMin)} –{" "}
                    {numberFormatter.format(results.extraPatientsMax)} patients/year
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: { xs: "11.5px", md: "12px" },
                      fontWeight: 400,
                      color: "rgba(255,255,255,0.70)",
                      mt: 0.75,
                      lineHeight: 1.5,
                    }}
                  >
                    With clearer reports, your repeat patients grow by an extra 5%–20%,
                    bringing even more people back for their next test.
                  </Typography>
                </Box>

                {/* Extra revenue tile */}
                <Box
                  sx={{
                    mt: { xs: 1.75, md: 2 },
                    p: { xs: 1.75, md: 2 },
                    borderRadius: "14px",
                    background: "rgba(255,255,255,0.07)",
                    border: "1px solid rgba(255,255,255,0.14)",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: { xs: "12px", md: "13px" },
                      fontWeight: 700,
                      color: "#34D399",
                      mb: 0.75,
                    }}
                  >
                    Extra revenue you could earn a year
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: { xs: "17px", sm: "19px", md: "21px" },
                      fontWeight: 700,
                      color: "#FFFFFF",
                      lineHeight: 1.15,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {currencyFormatter.format(results.extraRevenueMin)} –{" "}
                    {currencyFormatter.format(results.extraRevenueMax)}
                  </Typography>
                </Box>

                {/* CTA */}
                <Button
                  fullWidth
                  onClick={onBookDemo}
                  endIcon={<ArrowRight size={17} />}
                  sx={{
                    mt: { xs: 1.75, md: 2 },
                    position: "relative",
                    zIndex: 1,
                    width: "100% !important",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: { xs: "14px", md: "15px" },
                    fontWeight: 700,
                    color: "#fff",
                    background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                    padding: { xs: "10px", md: "11px" },
                    borderRadius: "10px",
                    textTransform: "none",
                    boxShadow: "0 8px 24px rgba(16,185,129,0.35)",
                    transition: "all 0.25s ease",
                    "&:hover": {
                      background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 30px rgba(16,185,129,0.45)",
                    },
                  }}
                >
                  Book a Demo
                </Button>

                {/* Sub-CTA line */}
                <Typography
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: { xs: "11px", md: "11.5px" },
                    fontWeight: 500,
                    color: "rgba(255,255,255,0.70)",
                    textAlign: "center",
                    mt: 1.25,
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  Free 30-min demo · No commitment required
                </Typography>

                {/* Info note */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    mt: 1.75,
                    p: { xs: 1.5, md: 1.5 },
                    borderRadius: "10px",
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.10)",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  <Info size={14} color="#5EEAD4" style={{ flexShrink: 0, marginTop: 2 }} />
                  <Typography
                    sx={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: { xs: "10.5px", md: "11px" },
                      color: "rgba(255,255,255,0.65)",
                      lineHeight: 1.6,
                    }}
                  >
                    We estimate Smart Reports grow your existing repeat patients by an
                    extra 5%–20%, then multiply that by your revenue per patient. Actual
                    results vary by lab.
                  </Typography>
                </Box>
              </ResultsPanel>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default RoiCalculatorSection;
